import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Bell, Info, ShieldAlert,
    Activity, AlertTriangle, AlertCircle, CheckCircle2,
    Server, SquareArrowOutUpRight, X, ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import baseapi from '../../services/baseapi';
import gocreateSocket from '../../services/gosocketapi';
import { useParams } from 'react-router-dom';

interface EventMonitorProps {
    assetIds: number[];
    onEventClick?: (assetId: number) => void;
    onEventsUpdate?: (events: FacilityEvent[]) => void;
}

interface FacilityEvent {
    id: number;
    asset_id: number | null;
    asset_hostname: string | null;
    asset_ip: string | null;
    agent_name: string;
    client_name: string;
    event_type: string;
    severity: string;
    message: string;
    description: string;
    event_status: string;
    event_data: string;
    ticket_id: number | null;
    ticket_number: string | null;
    created: string;
    updated: string;
    [key: string]: any;
}

// --- Helpers (mirrors EventsTable.tsx) ---
const getSeverityConfig = (severity: string) => {
    const s = severity?.toLowerCase() || '';
    if (s === 'critical') return { color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', icon: AlertTriangle, dot: 'bg-red-500' };
    if (s === 'high' || s === 'error') return { color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30', icon: AlertCircle, dot: 'bg-orange-500' };
    if (s === 'medium' || s === 'warning') return { color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30', icon: AlertCircle, dot: 'bg-yellow-500' };
    if (s === 'low' || s === 'info') return { color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/30', icon: Info, dot: 'bg-blue-500' };
    if (s === 'success') return { color: 'text-green-400', bg: 'bg-green-500/15', border: 'border-green-500/30', icon: CheckCircle2, dot: 'bg-green-500' };
    return { color: 'text-gray-400', bg: 'bg-gray-500/15', border: 'border-gray-500/30', icon: Info, dot: 'bg-gray-500' };
};

const formatTimestamp = (ts: string) => {
    if (!ts) return '-';
    try {
        return new Date(ts).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
        });
    } catch { return ts; }
};

export default function EventMonitor({ assetIds, onEventClick, onEventsUpdate }: EventMonitorProps) {
    const [events, setEvents] = useState<FacilityEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<FacilityEvent | null>(null);
    const { clientId } = useParams();

    useEffect(() => {
        if (!assetIds || assetIds.length === 0 || !clientId) return;

        const fetchEvents = async () => {
            setLoading(true);
            try {
                const query = {
                    asset_id: { $in: assetIds },
                    event_status: 'open',
                };
                const res = await baseapi.get('/events', {
                    params: { query: JSON.stringify(query), limit: 20 },
                });
                if (res.data.status) {
                    const fetchedEvents = res.data.data || [];
                    console.log("fetchedEvents", fetchedEvents)
                    const feExpiredEvent: FacilityEvent = {
                        id: 9999,
                        asset_id: -999,
                        asset_hostname: 'Fire Safety',
                        asset_ip: 'Location: Zone A',
                        agent_name: 'Safety Monitor',
                        client_name: 'Facility',
                        event_type: 'fire_ext_expired',
                        severity: 'critical',
                        message: 'Fire extinguisher has expired please change it',
                        description: 'Manual safety inspection flagged unit DU-FEXT-007 as expired. Immediate replacement required to maintain compliance.',
                        event_status: 'open',
                        event_data: JSON.stringify([{
                            status: 'open',
                            created: new Date().toISOString(),
                            data: {
                                unit_id: 'DU-FEXT-007',
                                expiry_date: '2024-05-14',
                                failure_reason: 'status_expired'
                            }
                        }]),
                        ticket_id: null,
                        ticket_number: null,
                        created: new Date().toISOString(),
                        updated: new Date().toISOString()
                    };
                    setEvents([feExpiredEvent, ...fetchedEvents]);
                }
            } catch (err) {
                console.error('Failed to fetch events', err);
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();

        const socket = gocreateSocket(`events?client_id=${clientId}&asset_ids=${JSON.stringify(assetIds)}`);

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.table === 'events') {
                    const record = data.record;
                    const isRelevant = assetIds.includes(record.asset_id);
                    const isOpen = record.event_status !== 'closed';

                    if (isRelevant) {
                        if (data.type === 'insert' && isOpen) {
                            setEvents(prev => {
                                if (prev.some(e => e.id === record.id)) return prev;
                                return [record, ...prev];
                            });
                        } else if (data.type === 'update') {
                            if (isOpen) {
                                setEvents(prev => prev.map(e => e.id === record.id ? record : e));
                            } else {
                                // If updated to closed, remove it
                                setEvents(prev => prev.filter(e => e.id !== record.id));
                            }
                        }
                    }
                }
            } catch (err) {
                console.error('WebSocket message error:', err);
            }
        };

        return () => {
            socket.close();
        };
    }, [JSON.stringify(assetIds), clientId]);

    useEffect(() => {
        onEventsUpdate?.(events);
    }, [events, onEventsUpdate]);

    const criticalCount = events.filter(e => e.severity.toLowerCase() === 'critical').length;

    return (
        <>
            <div className="flex flex-col gap-1 items-end pointer-events-auto">
                {/* Header / Trigger */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`flex items-center gap-3 px-3 py-1.5 rounded-lg border transition-all duration-300 group
                        ${isOpen
                            ? 'bg-gray-900/90 border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.15)]'
                            : 'bg-black/40 backdrop-blur-md border-white/5 hover:border-white/20 hover:bg-black/60 shadow-lg'
                        }`}
                >
                    <div className={`p-1 rounded-md transition-colors duration-300 ${criticalCount > 0 ? 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.4)] animate-pulse' : (isOpen ? 'bg-blue-500' : 'bg-gray-800')}`}>
                        <Bell className={`w-3.5 h-3.5 ${isOpen || criticalCount > 0 ? 'text-white' : 'text-blue-500'}`} />
                    </div>

                    <div className="flex flex-col items-end min-w-[110px]">
                        <span className="text-[8px] font-black uppercase tracking-[0.15em] text-gray-400 group-hover:text-gray-300 transition-colors">Alert Monitor</span>
                        <span className="text-xs font-black text-white leading-tight flex items-center gap-1.5">
                            {events.length} Events
                            {criticalCount > 0 && (
                                <span className="bg-red-500 text-white text-[8px] px-1 rounded-full font-black">
                                    {criticalCount} CRITICAL
                                </span>
                            )}
                        </span>
                    </div>
                </button>

                {/* Dropdown */}
                {isOpen && (
                    <div className="w-[250px] bg-gray-900/95 backdrop-blur-xl border border-white/5 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 z-[49]">
                        <div className="p-2.5 border-b border-white/5 bg-gradient-to-br from-blue-500/10 to-transparent flex items-center justify-between">
                            <h4 className="text-[9px] font-black text-white uppercase tracking-widest flex items-center gap-2">
                                Live Security Feed
                            </h4>
                            {loading && <span className="text-[8px] text-blue-400 animate-pulse font-black uppercase tracking-widest">Updating...</span>}
                        </div>

                        <div className="max-h-[400px] overflow-y-auto custom-scroll p-1.5 flex flex-col gap-1.5">
                            {events.length === 0 ? (
                                <div className="py-8 text-center">
                                    <p className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">No active alerts</p>
                                </div>
                            ) : (
                                events.map((event) => (
                                    <div
                                        key={event.id}
                                        onClick={() => onEventClick?.(event.asset_id!)}
                                        className="bg-white/5 border border-white/5 rounded-lg px-3 py-2.5 hover:bg-white/10 transition-all group relative cursor-pointer"
                                    >
                                        <div
                                            className={`absolute left-0 top-0 w-1 h-full rounded-l-lg ${event.severity.toLowerCase() === "critical"
                                                    ? "bg-red-500"
                                                    : event.severity.toLowerCase() === "warning"
                                                        ? "bg-amber-500"
                                                        : "bg-blue-500"
                                                }`}
                                        />

                                        <div className="flex items-start gap-2.5">
                                            <div
                                                className={`p-1.5 rounded-lg border flex-shrink-0 ${event.severity.toLowerCase() === "critical"
                                                        ? "bg-red-500/10 text-red-500 border-red-500/20"
                                                        : "bg-blue-500/10 text-blue-400 border-blue-500/20"
                                                    }`}
                                            >
                                                {event.severity.toLowerCase() === "critical" ? (
                                                    <ShieldAlert className="w-3.5 h-3.5" />
                                                ) : (
                                                    <Info className="w-3.5 h-3.5" />
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="text-[11px] text-white uppercase tracking-tight leading-none max-w-[110px] break-words whitespace-normal">
                                                        {event.asset_hostname || "System Alert"}
                                                    </p>

                                                    <span
                                                        className={`text-[8px] font-black uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${event.severity.toLowerCase() === "critical"
                                                                ? "text-red-500 border-red-500/30 bg-red-500/5"
                                                                : event.severity.toLowerCase() === "warning"
                                                                    ? "text-amber-500 border-amber-500/30 bg-amber-500/5"
                                                                    : "text-blue-400 border-blue-500/30 bg-blue-500/5"
                                                            }`}
                                                    >
                                                        {event.severity}
                                                    </span>
                                                </div>

                                                <p className="text-[10px] font-semibold text-gray-200 leading-tight">
                                                    {event.event_type.replace(/_/g, " ")}
                                                </p>
                                            </div>

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setSelectedEvent(event)
                                                }}
                                                className="flex-shrink-0 p-1 rounded-md bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-indigo-500/20 hover:border-indigo-500/30 transition-all self-center"
                                                title="View details"
                                            >
                                                <ChevronRight className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Event Detail Slide-out Sidebar — rendered via portal to escape pointer-events-none parent */}
            {createPortal(<AnimatePresence>
                {selectedEvent && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedEvent(null)}
                            className="fixed inset-0 bg-black/40 z-40"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-[620px] bg-slate-900 border-l border-slate-800 z-50 flex flex-col shadow-2xl"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-indigo-500/5">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded bg-indigo-500/15 text-indigo-400">
                                        <Activity className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-bold text-slate-100 leading-tight">
                                            {selectedEvent.asset_hostname || selectedEvent.agent_name || 'System Alert'}
                                        </p>
                                        <p className="text-[9px] text-gray-100 font-mono">{selectedEvent.asset_ip}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${selectedEvent.event_type === 'camera_offline'
                                        ? 'text-rose-400 bg-rose-500/10 border-rose-500/20'
                                        : selectedEvent.event_type === 'camera_online'
                                            ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                                            : 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20'
                                        }`}>
                                        {selectedEvent.event_type.replace(/_/g, ' ')}
                                    </span>
                                    <button
                                        onClick={() => setSelectedEvent(null)}
                                        className="p-1.5 rounded-lg text-gray-100 hover:text-slate-200 hover:bg-slate-800 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Event Details */}
                            <div className="flex-1 min-h-0 overflow-y-auto custom-scroll p-4 flex flex-col gap-3 pb-20">
                                {/* Badges row */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {selectedEvent.event_status && (
                                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${selectedEvent.event_status === 'closed'
                                            ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                                            : 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                                            }`}>
                                            {selectedEvent.event_status}
                                        </span>
                                    )}
                                    {(() => {
                                        const sevConfig = getSeverityConfig(selectedEvent.severity);
                                        const SevIcon = sevConfig.icon;
                                        return (
                                            <span className={`inline-flex items-center gap-1.5 text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${sevConfig.bg} ${sevConfig.color} ${sevConfig.border}`}>
                                                <SevIcon className="w-3 h-3" />
                                                {selectedEvent.severity}
                                            </span>
                                        );
                                    })()}
                                    {selectedEvent.ticket_number && (
                                        <span
                                            className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded border text-blue-400 bg-blue-500/10 border-blue-500/20 uppercase tracking-wider cursor-pointer"
                                            onClick={() => window.open(`${localStorage.getItem('ticketing_domain')}/tickets/${selectedEvent.ticket_id}`, '_blank')}
                                        >
                                            <SquareArrowOutUpRight className="w-3 h-3 flex-shrink-0" />
                                            #{selectedEvent.ticket_number}
                                        </span>
                                    )}
                                    <span className="text-[9px] text-gray-100 font-mono ml-auto">
                                        {formatTimestamp(`${selectedEvent.created}Z`)}
                                    </span>
                                </div>

                                {/* Message */}
                                <div className="rounded-lg border border-slate-800 bg-slate-800/30 p-3">
                                    <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Message</p>
                                    <p className="text-[11px] text-gray-100 leading-relaxed">{selectedEvent.message || '-'}</p>
                                </div>

                                {/* Description */}
                                <div className="rounded-lg border border-slate-800 bg-slate-800/30 p-3">
                                    <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Description</p>
                                    <p className="text-[10px] text-gray-100 leading-relaxed">{selectedEvent.description || '-'}</p>
                                </div>

                                {/* Agent */}
                                {selectedEvent.agent_name && (
                                    <div className="rounded-lg border border-slate-800 bg-slate-800/30 p-3">
                                        <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Agent</p>
                                        <p className="text-[10px] text-gray-100 truncate" title={selectedEvent.agent_name}>
                                            {selectedEvent.agent_name}
                                        </p>
                                    </div>
                                )}

                                {/* Event Data (Timeline) */}
                                {selectedEvent.event_data && (
                                    <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4 shadow-inner">
                                        <p className="text-[10px] font-bold text-gray-100 uppercase tracking-widest mb-3 border-b border-slate-800/50 pb-2">Event Metrics &amp; Details</p>
                                        {(() => {
                                            try {
                                                let parsedData = JSON.parse(selectedEvent.event_data);
                                                if (typeof parsedData === 'string') parsedData = JSON.parse(parsedData);
                                                const timelineEvents = Array.isArray(parsedData) ? parsedData : (typeof parsedData === 'object' && parsedData !== null ? [parsedData] : []);

                                                if (timelineEvents.length === 0) {
                                                    return (
                                                        <pre className="text-[9px] text-slate-400 font-mono leading-relaxed whitespace-pre-wrap break-all bg-slate-900/50 p-3 rounded border border-slate-800/50">
                                                            {selectedEvent.event_data}
                                                        </pre>
                                                    );
                                                }

                                                return (
                                                    <div className="relative border-l border-slate-700 ml-2 pl-3 py-1 space-y-3">
                                                        {timelineEvents.map((evtLog: any, idx: number) => {
                                                            const d = evtLog.data || {};
                                                            const status = evtLog.status || 'info';
                                                            const isClosed = status.toLowerCase() === 'closed';
                                                            const isOpen = status.toLowerCase() === 'open';
                                                            const badgeColor = isClosed ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20'
                                                                : isOpen ? 'text-rose-400 bg-rose-500/10 border border-rose-500/20'
                                                                    : 'text-amber-400 bg-amber-500/10 border border-amber-500/20';
                                                            const dotColor = isClosed ? 'bg-emerald-500 ring-slate-900 shadow-[0_0_8px_rgba(16,185,129,0.8)]'
                                                                : isOpen ? 'bg-rose-500 ring-slate-900 shadow-[0_0_8px_rgba(244,63,94,0.8)] animate-pulse'
                                                                    : 'bg-amber-500 ring-slate-900 shadow-[0_0_8px_rgba(245,158,11,0.8)]';
                                                            let eventDescription = '';
                                                            if (isOpen) {
                                                                eventDescription = selectedEvent.event_type.includes('offline')
                                                                    ? 'System detected asset went offline. Incident opened.'
                                                                    : 'Event triggered and incident opened for review.';
                                                            } else if (isClosed) {
                                                                eventDescription = (selectedEvent.event_type.includes('offline') || selectedEvent.event_type.includes('online'))
                                                                    ? 'Asset came back online. Incident resolved and closed.'
                                                                    : 'Incident was resolved and marked as closed.';
                                                            } else {
                                                                eventDescription = 'Status updated to ' + status;
                                                            }
                                                            const formattedDate = evtLog.created
                                                                ? new Date(`${evtLog.created}${!evtLog.created.endsWith('Z') && !evtLog.created.includes('+') ? 'Z' : ''}`).toLocaleString(undefined, {
                                                                    month: 'short', day: 'numeric', year: 'numeric',
                                                                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                                                                })
                                                                : 'Unknown Time';

                                                            return (
                                                                <div key={idx} className="relative group">
                                                                    <div className="absolute -left-[16px] top-2.5 flex items-center justify-center z-10">
                                                                        <div className={`w-1.5 h-1.5 rounded-full ring-4 ${dotColor}`} />
                                                                    </div>
                                                                    <div className={`bg-slate-900/60 rounded-xl border p-2.5 transition-all duration-300 ${isClosed ? 'border-emerald-500/20 hover:border-emerald-500/40 hover:bg-emerald-950/20'
                                                                        : isOpen ? 'border-rose-500/20 hover:border-rose-500/40 hover:bg-rose-950/20'
                                                                            : 'border-slate-800/80 hover:border-slate-700/80 hover:bg-slate-800/40'
                                                                        }`}>
                                                                        <div className="flex items-center justify-between mb-1.5 border-b border-slate-800/50 pb-1.5">
                                                                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest ${badgeColor}`}>{status}</span>
                                                                            <span className="text-[9px] text-gray-100 font-mono tracking-tight bg-slate-950/50 px-1.5 py-0.5 rounded border border-slate-800/50 shadow-inner">{formattedDate}</span>
                                                                        </div>
                                                                        <div className="flex items-start gap-1.5 mb-1 text-[10px]">
                                                                            <div className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${isClosed ? 'bg-emerald-500' : isOpen ? 'bg-rose-500' : 'bg-amber-500'}`} />
                                                                            <p className="text-slate-300 font-medium leading-relaxed">{eventDescription}</p>
                                                                        </div>
                                                                        {Object.keys(d).length > 0 && (
                                                                            <div className="bg-slate-950/60 rounded-lg border border-slate-800/50 p-2 mt-2 grid grid-cols-2 gap-y-2 gap-x-3 shadow-inner">
                                                                                {d.offline_start && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Offline Detected</span>
                                                                                        <span className="text-[9px] text-gray-100 font-mono mt-0.5">{new Date(`${d.offline_start}`).toLocaleString()}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.online_again && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Back Online</span>
                                                                                        <span className="text-[9px] text-emerald-400 font-mono mt-0.5">{new Date(`${d.online_again}`).toLocaleString()}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.duration_minutes !== undefined && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Total Downtime</span>
                                                                                        <span className="text-[9px] text-indigo-400 font-bold mt-0.5">{d.duration_minutes} Minutes</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.failure_reason && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Root Cause</span>
                                                                                        <span className="text-[9px] text-rose-400 font-mono mt-0.5 tabular-nums">{d.failure_reason.replace(/_/g, ' ')}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.ip_address && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Source IP</span>
                                                                                        <span className="text-[9px] text-gray-100 font-mono mt-0.5">{d.ip_address}</span>
                                                                                    </div>
                                                                                )}
                                                                                {Object.entries(d)
                                                                                    .filter(([k]) => !['offline_start', 'online_again', 'duration_minutes', 'failure_reason', 'ip_address', 'ticket_description_html', 'warning_issues', 'critical_issues', 'assets'].includes(k))
                                                                                    .map(([k, v]) => (
                                                                                        <div className="flex flex-col" key={k}>
                                                                                            <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">{k.replace(/_/g, ' ')}</span>
                                                                                            <span className="text-[9px] text-gray-100 font-mono mt-0.5 overflow-hidden text-ellipsis truncate" title={typeof v === 'object' ? JSON.stringify(v) : String(v)}>
                                                                                                {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (typeof v === 'object' ? JSON.stringify(v) : String(v))}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))
                                                                                }
                                                                                {(d.critical_issues && Array.isArray(d.critical_issues) && d.critical_issues.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-slate-800/50 pt-3">
                                                                                        <span className="text-[10px] text-rose-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <AlertTriangle className="w-3.5 h-3.5" />
                                                                                            Critical Issues ({d.critical_issues.length})
                                                                                        </span>
                                                                                        <div className="flex flex-col gap-1.5">
                                                                                            {d.critical_issues.map((iss: any, i: number) => (
                                                                                                <div key={i} className="bg-rose-950/20 border border-rose-900/30 rounded p-2.5">
                                                                                                    <div className="flex items-start justify-between mb-1 gap-2">
                                                                                                        <span className="text-[10px] font-bold text-rose-300 leading-tight">{iss.issue}</span>
                                                                                                        <span className="text-[8px] px-1.5 py-0.5 bg-rose-500/20 text-rose-300 rounded uppercase shrink-0">{iss.category}</span>
                                                                                                    </div>
                                                                                                    <p className="text-[9px] text-gray-400 leading-relaxed">{iss.description}</p>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                                {(d.warning_issues && Array.isArray(d.warning_issues) && d.warning_issues.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-slate-800/50 pt-3">
                                                                                        <span className="text-[10px] text-amber-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <AlertCircle className="w-3.5 h-3.5" />
                                                                                            Warning Issues ({d.warning_issues.length})
                                                                                        </span>
                                                                                        <div className="flex flex-col gap-1.5">
                                                                                            {d.warning_issues.map((iss: any, i: number) => (
                                                                                                <div key={i} className="bg-amber-950/20 border border-amber-900/30 rounded p-2.5">
                                                                                                    <div className="flex items-start justify-between mb-1 gap-2">
                                                                                                        <span className="text-[10px] font-bold text-amber-300 leading-tight">{iss.issue}</span>
                                                                                                        <span className="text-[8px] px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded uppercase shrink-0">{iss.category}</span>
                                                                                                    </div>
                                                                                                    <p className="text-[9px] text-gray-400 leading-relaxed">{iss.description}</p>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                                {(d.assets && Array.isArray(d.assets) && d.assets.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-slate-800/50 pt-3">
                                                                                        <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <Server className="w-3.5 h-3.5" />
                                                                                            Assets ({d.assets.length})
                                                                                        </span>
                                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                                                                            {d.assets.map((asset: any, i: number) => (
                                                                                                <div key={i} className="bg-emerald-950/20 border border-emerald-900/30 rounded p-2.5 flex items-center justify-between">
                                                                                                    <div className="flex flex-col gap-0.5">
                                                                                                        <span className="text-[10px] font-bold text-emerald-300 leading-tight truncate max-w-[140px]" title={asset.name}>{asset.name || 'Unknown Asset'}</span>
                                                                                                        <span className="text-[9px] text-gray-400 font-mono">{asset.ip_address || 'No IP'}</span>
                                                                                                    </div>
                                                                                                    <span className="text-[8px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded uppercase shrink-0">{asset.asset_type || 'Unknown'}</span>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            } catch (e) {
                                                return (
                                                    <pre className="text-[9px] text-slate-400 font-mono leading-relaxed whitespace-pre-wrap break-all bg-slate-900/50 p-3 rounded border border-slate-800/50">
                                                        {selectedEvent.event_data}
                                                    </pre>
                                                );
                                            }
                                        })()}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>, document.body)}
        </>
    );
}
